import { ValidationError, formatValidationErrors } from './jobCardValidation';

// Error handling types
export interface JobCardError extends Error {
  code: string;
  field?: string;
  details?: any;
  timestamp: string;
}

export type ErrorSeverity = 'info' | 'warning' | 'error' | 'critical';

export interface ErrorHandlingOptions {
  showToast?: boolean;
  logToConsole?: boolean;
  logToAudit?: boolean;
  severity?: ErrorSeverity;
  retryable?: boolean;
}

// Common error codes
export const ERROR_CODES = {
  // Authentication & Authorization
  UNAUTHORIZED: 'UNAUTHORIZED',
  INSUFFICIENT_PERMISSIONS: 'INSUFFICIENT_PERMISSIONS',
  
  // Job Card State
  INVALID_JOB_STATUS: 'INVALID_JOB_STATUS',
  JOB_NOT_FOUND: 'JOB_NOT_FOUND',
  JOB_ALREADY_STARTED: 'JOB_ALREADY_STARTED',
  JOB_NOT_STARTED: 'JOB_NOT_STARTED',
  
  // Validation
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  INVALID_QUANTITY: 'INVALID_QUANTITY',
  EXCEEDS_PLANNED_QUANTITY: 'EXCEEDS_PLANNED_QUANTITY',
  
  // Operators
  OPERATOR_NOT_FOUND: 'OPERATOR_NOT_FOUND',
  OPERATOR_ALREADY_ASSIGNED: 'OPERATOR_ALREADY_ASSIGNED',
  OPERATOR_HAS_ACTIVE_TIME: 'OPERATOR_HAS_ACTIVE_TIME',
  
  // Materials
  MATERIAL_NOT_AVAILABLE: 'MATERIAL_NOT_AVAILABLE',
  INSUFFICIENT_INVENTORY: 'INSUFFICIENT_INVENTORY',
  
  // QC
  QC_CHECKPOINT_FAILED: 'QC_CHECKPOINT_FAILED',
  QC_HOLD_ACTIVE: 'QC_HOLD_ACTIVE',
  
  // Database
  DATABASE_ERROR: 'DATABASE_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  CONNECTION_FAILED: 'CONNECTION_FAILED',
  
  // File Upload
  FILE_TOO_LARGE: 'FILE_TOO_LARGE',
  INVALID_FILE_TYPE: 'INVALID_FILE_TYPE',
  UPLOAD_FAILED: 'UPLOAD_FAILED',
} as const;

// Error message mappings
const ERROR_MESSAGES: Record<string, string> = {
  [ERROR_CODES.UNAUTHORIZED]: 'You are not authorized to perform this action',
  [ERROR_CODES.INSUFFICIENT_PERMISSIONS]: 'You do not have permission to perform this action',
  [ERROR_CODES.INVALID_JOB_STATUS]: 'Invalid job status for this operation',
  [ERROR_CODES.JOB_NOT_FOUND]: 'Job card not found',
  [ERROR_CODES.JOB_ALREADY_STARTED]: 'Job card has already been started',
  [ERROR_CODES.JOB_NOT_STARTED]: 'Job card has not been started yet',
  [ERROR_CODES.VALIDATION_FAILED]: 'Input validation failed',
  [ERROR_CODES.INVALID_QUANTITY]: 'Invalid quantity entered',
  [ERROR_CODES.EXCEEDS_PLANNED_QUANTITY]: 'Quantity exceeds planned amount',
  [ERROR_CODES.OPERATOR_NOT_FOUND]: 'Operator not found',
  [ERROR_CODES.OPERATOR_ALREADY_ASSIGNED]: 'Operator is already assigned to this job',
  [ERROR_CODES.OPERATOR_HAS_ACTIVE_TIME]: 'Cannot modify operator with active time logs',
  [ERROR_CODES.MATERIAL_NOT_AVAILABLE]: 'Required materials are not available',
  [ERROR_CODES.INSUFFICIENT_INVENTORY]: 'Insufficient inventory to complete this operation',
  [ERROR_CODES.QC_CHECKPOINT_FAILED]: 'Quality control checkpoint failed',
  [ERROR_CODES.QC_HOLD_ACTIVE]: 'Job is on QC hold and cannot proceed',
  [ERROR_CODES.DATABASE_ERROR]: 'Database operation failed',
  [ERROR_CODES.NETWORK_ERROR]: 'Network connection error',
  [ERROR_CODES.CONNECTION_FAILED]: 'Failed to connect to server',
  [ERROR_CODES.FILE_TOO_LARGE]: 'File size exceeds maximum allowed',
  [ERROR_CODES.INVALID_FILE_TYPE]: 'File type is not allowed',
  [ERROR_CODES.UPLOAD_FAILED]: 'File upload failed',
};

/**
 * Create a standardized job card error
 */
export function createJobCardError(
  code: string,
  message?: string,
  field?: string,
  details?: any
): JobCardError {
  const error = new Error(message || ERROR_MESSAGES[code] || 'An unknown error occurred') as JobCardError;
  error.code = code;
  error.field = field;
  error.details = details;
  error.timestamp = new Date().toISOString();
  return error;
}

/**
 * Handle a job card error with appropriate logging and user feedback
 */
export function handleJobCardError(
  error: Error | JobCardError | ValidationError[],
  options: ErrorHandlingOptions = {}
): {
  code: string;
  message: string;
  shouldRetry: boolean;
} {
  const {
    showToast = true,
    logToConsole = true,
    logToAudit = true,
    severity = 'error',
    retryable = false,
  } = options;

  let code: string;
  let message: string;
  let shouldRetry = retryable;

  // Handle validation errors array
  if (Array.isArray(error)) {
    code = ERROR_CODES.VALIDATION_FAILED;
    message = formatValidationErrors(error);
    shouldRetry = false;
  }
  // Handle JobCardError
  else if ('code' in error && error.code) {
    code = error.code;
    message = error.message;
    
    // Determine if error is retryable based on code
    shouldRetry = retryable || isRetryableError(code);
  }
  // Handle generic Error
  else {
    code = ERROR_CODES.DATABASE_ERROR;
    message = error.message || 'An unknown error occurred';
    shouldRetry = retryable;
  }

  // Log to console if enabled
  if (logToConsole) {
    const logLevel = severity === 'critical' || severity === 'error' ? 'error' : 
                     severity === 'warning' ? 'warn' : 'info';
    console[logLevel](`[JobCard Error] ${code}: ${message}`, error);
  }

  // Log to audit trail if enabled (this would integrate with audit system)
  if (logToAudit) {
    // This would call the audit logging system
    // auditLogger.logError(code, message, error.details);
  }

  return {
    code,
    message,
    shouldRetry,
  };
}

/**
 * Determine if an error is retryable based on its code
 */
function isRetryableError(code: string): boolean {
  const retryableCodes = [
    ERROR_CODES.NETWORK_ERROR,
    ERROR_CODES.CONNECTION_FAILED,
    ERROR_CODES.DATABASE_ERROR,
  ];
  
  return retryableCodes.includes(code);
}

/**
 * Create error response for API functions
 */
export function createErrorResponse(
  error: Error | JobCardError | ValidationError[],
  options?: ErrorHandlingOptions
): { success: false; error: string; code: string; retryable: boolean } {
  const { code, message, shouldRetry } = handleJobCardError(error, options);
  
  return {
    success: false,
    error: message,
    code,
    retryable: shouldRetry,
  };
}

/**
 * Create success response for API functions
 */
export function createSuccessResponse<T>(
  data?: T,
  message?: string
): { success: true; data?: T; message?: string } {
  return {
    success: true,
    data,
    message,
  };
}

/**
 * Wrapper for async operations with error handling
 */
export async function withErrorHandling<T>(
  operation: () => Promise<T>,
  errorOptions?: ErrorHandlingOptions
): Promise<{ success: boolean; data?: T; error?: string; code?: string; retryable?: boolean }> {
  try {
    const data = await operation();
    return createSuccessResponse(data);
  } catch (error) {
    return createErrorResponse(error as Error, errorOptions);
  }
}

/**
 * Retry an operation with exponential backoff
 */
export async function retryOperation<T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> {
  let lastError: Error;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error as Error;
      
      // Don't retry if it's not a retryable error
      if ('code' in error && !isRetryableError((error as JobCardError).code)) {
        throw error;
      }
      
      // Don't wait after the last attempt
      if (attempt < maxRetries) {
        const delay = baseDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  throw lastError!;
}

/**
 * Validate user permissions for job card actions
 */
export function validateJobCardPermission(
  userRole: string,
  action: string,
  jobCardStatus?: string
): void {
  // This would integrate with the validation utility
  // For now, just a placeholder
  if (!userRole) {
    throw createJobCardError(ERROR_CODES.UNAUTHORIZED, 'User role not found');
  }
}

/**
 * Log job card operation for audit trail
 */
export function logJobCardOperation(
  jobCardId: string,
  operationType: string,
  userId: string,
  details?: any
): void {
  // This would integrate with the audit system
  console.log(`[JobCard Audit] ${operationType} on ${jobCardId} by ${userId}`, details);
}
